import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
    Box, Breadcrumbs, Button, CircularProgress, LinearProgress, Link, Step, StepButton, StepLabel, Stepper, Typography,
} from "@mui/material";
import HomeIcon from "@mui/icons-material/Home";
import ArticleIcon from "@mui/icons-material/Article";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import axios from "axios";
import HeaderDashboard from "../../components/HeaderDashboard";
import ModalNotificacao from "./ModalNotificacao";
import AlertSuccess from "../../components/AlertSuccess";
import AlertError from "../../components/AlertError";
import { useUser } from "../../../contexts/UserContext";
import {
    camposPendentesFicha, camposPendentesSocio, documentosAprovados, progressoFicha, somaParticipacoes,
} from "../../../utils/FichaCadastral";
import DadosEmpresaFicha from "./DadosEmpresaFicha";
import SociosFicha from "./SociosFicha";
import DocumentosSocios from "./DocumentosSocios";
import { cardSx } from "./EstilosFicha";

const ETAPAS = [
    { chave: "empresa", label: "Dados da empresa" },
    { chave: "socios", label: "Sócios" },
    { chave: "documentos", label: "Documentos" },
];

const sociosCompletos = (socios) =>
    socios.length > 0 && somaParticipacoes(socios) === 100 && socios.every((s) => camposPendentesSocio(s).length === 0);

// Primeira etapa que ainda tem pendência
const etapaInicial = (ficha, socios) => {
    if (!ficha || camposPendentesFicha(ficha).length > 0) return 0;
    if (!sociosCompletos(socios)) return 1;
    return 2;
};

// Ficha cadastral em etapas: empresa -> sócios (exige a ficha) -> documentos (exige sócios).
// Aceita ?etapa=empresa|socios|documentos para abrir direto numa etapa.
const FichaCadastral = () => {
    const { user } = useUser();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const [userActive, setUserActive] = useState(true);
    const [openModalNotificacao, setOpenModalNotificacao] = useState(false);
    const [notificacoes, setNotificacoes] = useState([]);

    const [ficha, setFicha] = useState(null);
    const [socios, setSocios] = useState([]);
    const [carregando, setCarregando] = useState(true);
    const [etapa, setEtapa] = useState(0);

    const [openSnackbarSuccess, setOpenSnackbarSuccess] = useState(false);
    const [openSnackbarError, setOpenSnackbarError] = useState(false);
    const [mensagem, setMensagem] = useState("");

    useEffect(() => {
        const hasFaturasVencidas = user?.faturas?.vencidas?.length > 0;
        setUserActive(!hasFaturasVencidas);
    }, [user]);

    useEffect(() => {
        const getNotify = async () => {
            const response = await axios.post(`${process.env.REACT_APP_API_URL}/buscarNotificacoes`, { email: localStorage.getItem("email") }, {
                headers: {
                    Authorization: `${localStorage.getItem("token")}`
                }
            });

            if (response.data.status === 200) {
                setNotificacoes(response.data.notificacoes)
            }
        }

        getNotify();
    }, []);

    // Retorna os dados carregados para quem precisa decidir a etapa logo em seguida
    const carregar = useCallback(async () => {
        const config = { headers: { Authorization: localStorage.getItem("token") } };
        try {
            const [respostaFicha, respostaSocios] = await Promise.all([
                axios.get(`${process.env.REACT_APP_API_URL}/obterFichaCadastral`, config),
                axios.get(`${process.env.REACT_APP_API_URL}/listarSocios`, config),
            ]);
            const fichaAtual = respostaFicha.data.status === 200 ? respostaFicha.data.ficha : null;
            const sociosAtuais = respostaSocios.data.status === 200 ? respostaSocios.data.socios ?? [] : [];
            setFicha(fichaAtual);
            setSocios(sociosAtuais);
            return { ficha: fichaAtual, socios: sociosAtuais };
        } catch (error) {
            setMensagem("Não foi possível carregar a ficha cadastral.");
            setOpenSnackbarError(true);
            return { ficha: null, socios: [] };
        }
    }, []);

    useEffect(() => {
        const iniciar = async () => {
            const dados = await carregar();
            const pedida = ETAPAS.findIndex((e) => e.chave === searchParams.get("etapa"));
            const liberadas = !dados.ficha ? 0 : dados.socios.length === 0 ? 1 : 2;
            setEtapa(pedida >= 0 ? Math.min(pedida, liberadas) : etapaInicial(dados.ficha, dados.socios));
            setCarregando(false);
        };
        iniciar();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [carregar]);

    const sucesso = (texto) => {
        setMensagem(texto);
        setOpenSnackbarSuccess(true);
    };

    const erro = (texto) => {
        setMensagem(texto);
        setOpenSnackbarError(true);
    };

    const etapaBloqueada = (indice) => (indice === 1 && !ficha) || (indice === 2 && socios.length === 0);

    const etapaConcluida = [
        Boolean(ficha) && camposPendentesFicha(ficha).length === 0,
        sociosCompletos(socios),
        socios.length > 0 && socios.every(documentosAprovados),
    ];

    const progresso = progressoFicha(ficha, socios);

    const descricaoBloqueio = (indice) =>
        indice === 1 ? "Salve os dados da empresa primeiro" : "Adicione pelo menos um sócio";

    return (
        <>
            <HeaderDashboard userActive={userActive} setOpenModalNotificacao={setOpenModalNotificacao} notificacoes={notificacoes.length} />
            <Box sx={{ pt: "80px", pb: 4, px: { xs: 2, md: "5%" }, minHeight: "100vh" }}>
                <Box sx={{ ...cardSx, display: "flex", flexDirection: { xs: "column", md: "row" }, alignItems: { xs: "stretch", md: "center" }, justifyContent: "space-between", gap: 2 }}>
                    <Box>
                        <Breadcrumbs aria-label="breadcrumb" sx={{ fontSize: 12, "& .MuiBreadcrumbs-separator": { mx: 0.5 } }}>
                            <Link
                                underline="hover"
                                sx={{ display: "flex", alignItems: "center", cursor: "pointer", fontSize: 12, color: "#6b7280" }}
                                onClick={() => navigate("../Dashboard")}
                            >
                                <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                                Dashboard
                            </Link>
                            <Typography sx={{ fontSize: 12, color: "#111827", fontWeight: 600, display: "flex", alignItems: "center" }}>
                                <ArticleIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                                Ficha Cadastral
                            </Typography>
                        </Breadcrumbs>
                        <Typography sx={{ mt: 1, fontSize: { xs: 20, md: 24 }, fontWeight: 700, color: "#111827" }}>
                            Ficha cadastral
                        </Typography>
                        <Typography sx={{ mt: 0.5, fontSize: 14, color: "#4b5563" }}>
                            Informe os dados da empresa, cadastre os sócios e envie os documentos de cada um.
                        </Typography>
                    </Box>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1, minWidth: { md: 260 } }}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
                            <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>Progresso da ficha</Typography>
                            <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#111827" }}>{carregando ? "..." : `${progresso}%`}</Typography>
                        </Box>
                        <LinearProgress
                            variant="determinate"
                            value={carregando ? 0 : progresso}
                            sx={{ height: 6, borderRadius: 3, backgroundColor: "#e5e7eb", "& .MuiLinearProgress-bar": { backgroundColor: progresso === 100 ? "#16a34a" : "#0b243d", borderRadius: 3 } }}
                        />
                        <Button
                            startIcon={<ArrowBackIcon />}
                            onClick={() => navigate("../Dashboard")}
                            sx={{ alignSelf: { xs: "stretch", md: "flex-end" }, mt: 0.5, textTransform: "none", fontSize: 13, fontWeight: 600, color: "#374151", backgroundColor: "#f3f4f6", borderRadius: "8px", px: 2, "&:hover": { backgroundColor: "#e5e7eb" } }}
                        >
                            Voltar
                        </Button>
                    </Box>
                </Box>

                {carregando ? (
                    <Box sx={{ ...cardSx, mt: 2, display: "flex", justifyContent: "center", py: 8 }}>
                        <CircularProgress size={28} sx={{ color: "#0b243d" }} />
                    </Box>
                ) : (
                    <>
                        <Box sx={{ ...cardSx, mt: 2, py: { xs: 1.5, md: 2 } }}>
                            <Stepper
                                nonLinear
                                activeStep={etapa}
                                alternativeLabel
                                sx={{
                                    "& .MuiStepIcon-root.Mui-active": { color: "#0b243d" },
                                    "& .MuiStepIcon-root.Mui-completed": { color: "#16a34a" },
                                    "& .MuiStepLabel-label": { fontSize: 13, mt: "6px !important" },
                                }}
                            >
                                {ETAPAS.map((item, indice) => (
                                    <Step key={item.chave} completed={etapaConcluida[indice]} disabled={etapaBloqueada(indice)}>
                                        <StepButton onClick={() => setEtapa(indice)} disabled={etapaBloqueada(indice)}>
                                            <StepLabel
                                                optional={etapaBloqueada(indice)
                                                    ? <Typography sx={{ fontSize: 11, color: "#9ca3af", textAlign: "center" }}>{descricaoBloqueio(indice)}</Typography>
                                                    : null}
                                            >
                                                {item.label}
                                            </StepLabel>
                                        </StepButton>
                                    </Step>
                                ))}
                            </Stepper>
                        </Box>

                        <Box sx={{ mt: 2 }}>
                            {etapa === 0 && (
                                <DadosEmpresaFicha
                                    ficha={ficha}
                                    onSalvo={async (texto) => { await carregar(); sucesso(texto); }}
                                    onErro={erro}
                                    onAvancar={() => setEtapa(1)}
                                />
                            )}
                            {etapa === 1 && ficha && (
                                <SociosFicha
                                    socios={socios}
                                    onAtualizar={carregar}
                                    onSucesso={sucesso}
                                    onErro={erro}
                                    onAvancar={() => setEtapa(2)}
                                />
                            )}
                            {etapa === 2 && socios.length > 0 && (
                                <DocumentosSocios socios={socios} onAtualizar={carregar} onSucesso={sucesso} onErro={erro} />
                            )}
                        </Box>
                    </>
                )}
            </Box>
            <ModalNotificacao
                openModalNotificacao={openModalNotificacao}
                onClose={() => setOpenModalNotificacao(false)}
                notificacoes={notificacoes}
            />
            <AlertSuccess openSnackbarSuccess={openSnackbarSuccess} setOpenSnackbarSuccess={setOpenSnackbarSuccess} mensagem={mensagem} />
            <AlertError openSnackbarError={openSnackbarError} setOpenSnackbarError={setOpenSnackbarError} mensagem={mensagem} />
        </>
    );
};

export default FichaCadastral;
